import React, { type SyntheticEvent, useEffect, useRef } from 'react';
import { styled } from '@compiled/react';
import { Checkbox } from '@atlaskit/checkbox';
import { token } from '@atlaskit/tokens';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntlV2 } from '@atlassian/jira-intl/src/v2/use-intl.tsx';
import { IssueLinksEdit } from '@atlassian/jira-issue-field-issue-links/src/ui/edit/index.tsx';
import { mapDataToOptions } from '@atlassian/jira-issue-field-issue-links/src/ui/edit/utils.tsx';
import { FieldContainer } from '../../common/ui/fields/styled.tsx';
import { withFormField } from '../../common/ui/with-form-field/index.tsx';
import { closeMenuOnScroll } from '../../common/utils/select/index.tsx';
import messages from './messages.tsx';
import type {
	Props,
	IssueLinksFieldConfig,
	FieldValue,
	IssueLinksDefaultValueServer,
} from './types.tsx';
import { transformDefaultIssueLinksValue, validateIssueLinksField } from './utils.tsx';

export const IssueLinksField = (props: Props) => {
	const {
		autoCompleteUrl,
		width,
		fieldId,
		allowedValues,
		autoFocus = false,
		fieldProps: { value, onChange, id, isRequired },
		onCloseMenuOnScroll = closeMenuOnScroll,
		isDropdownMenuFixedAndLayered = false,
		isCreateLinkedIssue = false,
		additionalData,
	} = props;
	const { formatMessage } = useIntlV2();
	const options = mapDataToOptions(allowedValues);
	// According to the new implemented logic, the initial values of the checkboxes will be determined by the BE payload.
	// If the response from the BE is null, we wont display the checkbox
	// if it's false, we'll display it and mark it unchecked
	// it it's true, we'll display it and mark it checked
	const { copyLinks, copyAttachments } = additionalData?.copyLinksAndAttachments || {};

	const hasUpdatedInitialLinks = useRef(false);

	useEffect(() => {
		if (fg('jira-concurrent-create-linked-issue')) {
			/**
			 * With concurrent, onChange is changing every render and value is not updated,
			 * so we get an infinite loop in this useEffect.
			 *
			 * This ref is used to avoid this infinite loop.
			 */
			if (hasUpdatedInitialLinks.current) {
				return;
			}
		}
		if (isCreateLinkedIssue && fg('create_linked_issues_trigger_point_issue_view')) {
			if (value?.includeAttachments === undefined && value?.includeLinks === undefined) {
				onChange({
					...(value || {}),
					includeLinks: copyLinks || false,
					includeAttachments: copyAttachments || false,
				});
				if (fg('jira-concurrent-create-linked-issue')) {
					hasUpdatedInitialLinks.current = true;
				}
			}
		}
	}, [copyAttachments, copyLinks, isCreateLinkedIssue, onChange, value]);

	const onIssueLinksEditChange = (newValue: FieldValue) => {
		value &&
			onChange({
				...(value || {}),
				...newValue,
			});
	};
	const onIncludeAttachmentsChange = (e: SyntheticEvent<HTMLInputElement>) => {
		value &&
			onChange({
				...value,
				includeAttachments: e.currentTarget.checked,
			});
	};
	const onIncludeLinksChange = (e: SyntheticEvent<HTMLInputElement>) => {
		value &&
			onChange({
				...value,
				includeLinks: e.currentTarget.checked,
			});
	};
	return (
		<FieldContainer width={width}>
			<IssueLinksEdit
				linkTypeOptions={options}
				autoFocus={autoFocus}
				autoCompleteUrl={autoCompleteUrl}
				onChange={
					isCreateLinkedIssue && fg('create_linked_issues_trigger_point_issue_view')
						? onIssueLinksEditChange
						: onChange
				}
				fetchSuggestionsOnMount={false}
				value={value}
				fieldId={fieldId}
				onCloseMenuOnScroll={onCloseMenuOnScroll}
				isDropdownMenuFixedAndLayered={isDropdownMenuFixedAndLayered}
				inputId={id}
				isRequired={isRequired}
			/>
			{isCreateLinkedIssue && fg('create_linked_issues_trigger_point_issue_view') && (
				<CheckboxWrapper>
					{additionalData?.copyLinksAndAttachments !== null && (
						<Checkbox
							onChange={onIncludeLinksChange}
							label={formatMessage(messages.includeLinks)}
							isChecked={value?.includeLinks || false}
						/>
					)}
					{additionalData?.copyLinksAndAttachments !== null && (
						<Checkbox
							onChange={onIncludeAttachmentsChange}
							label={formatMessage(messages.includeAttachments)}
							isChecked={value?.includeAttachments || false}
						/>
					)}
				</CheckboxWrapper>
			)}
		</FieldContainer>
	);
};

// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports
export type { IssueLinksDefaultValueServer };

export default withFormField({
	transformDefaultValue: transformDefaultIssueLinksValue,
	validator: validateIssueLinksField,
	// @ts-expect-error - TS2345 - Argument of type '(props: Props) => JSX.Element' is not assignable to parameter of type 'AbstractComponent<ComponentProps<IssueLinksFieldConfig, IssueLink>, any>'.
})<IssueLinksFieldConfig, FieldValue>(IssueLinksField);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CheckboxWrapper = styled.div({
	display: 'flex',
	flexDirection: 'column',
	paddingTop: token('space.050'),
	paddingLeft: token('space.050'),
	gap: token('space.050'),
});
